import React, { useState, useEffect } from 'react';
import { BRAND } from '../lib/branding';
import { logger } from '../lib/logger';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Car, Clock, MapPin, CheckCircle,
  CreditCard, ChevronRight, Phone, Plane, ArrowRight,
  Users, Zap, Play, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { reservationsApi } from '../lib/api';
import { cn } from '../lib/utils';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { fadeInUp, fadeIn, scaleIn, viewportOnce, transitions } from '../lib/animations';

// Time options
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  }
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const locale = i18n.language === 'es' ? es : enUS;
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState('10:00');
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState('10:00');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate price when dates change
  useEffect(() => {
    const calculatePrice = async () => {
      if (!startDate || !endDate) return;
      
      const startDt = new Date(startDate);
      const [startH, startM] = startTime.split(':');
      startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
      
      const endDt = new Date(endDate);
      const [endH, endM] = endTime.split(':');
      endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
      
      if (endDt <= startDt) return;

      try {
        const response = await reservationsApi.calculatePrice(startDt.toISOString(), endDt.toISOString());
        setPriceData(response.data);
      } catch (error) {
        logger.error('Error:', error);
      }
    };
    calculatePrice();
  }, [startDate, endDate, startTime, endTime]);

  const handleReserve = () => {
    if (!startDate || !endDate) {
      toast.error('Selecciona las fechas de tu viaje');
      return;
    }
    
    // Store dates in sessionStorage and redirect to booking
    const startDt = new Date(startDate);
    const [startH, startM] = startTime.split(':');
    startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
    
    const endDt = new Date(endDate);
    const [endH, endM] = endTime.split(':');
    endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
    
    sessionStorage.setItem('booking_start', startDt.toISOString());
    sessionStorage.setItem('booking_end', endDt.toISOString());
    
    navigate('/reservar?from=landing');
  };

  const scrollToForm = () => {
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Minimal Header - No distractions */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/5">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand/cg-valet-parking.png" alt="CG Valet Parking" className="h-12 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="minimal" />
            <a 
              href={BRAND.phoneHref}
              aria-label={`Llamar a CG Valet Parking: ${BRAND.phone}`} 
              className="flex items-center gap-2 text-sm text-[#AAEE18] hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{BRAND.phone}</span>
            </a>
            {isAuthenticated ? (
              <Link 
                to="/account"
                className="flex items-center gap-2 text-sm text-white hover:text-[#AAEE18] transition-colors"
                data-testid="my-account-link"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.account')}</span>
              </Link>
            ) : (
              <Link 
                to="/login"
                className="flex items-center gap-2 text-sm text-white hover:text-[#AAEE18] transition-colors"
                data-testid="login-link"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ============================================
          HERO SECTION - HIGH CONVERSION VISUAL IMPACT
          ============================================ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video/Image Background */}
        <div className="absolute inset-0">
          {/* Airport terminal drop-off photo, cropped separately for mobile */}
          <picture className="absolute inset-0">
            <source
              media="(max-width: 767px)"
              srcSet="/brand/airport-valet-terminal-mobile.webp"
            />
            <img
              src="/brand/airport-valet-terminal-desktop.webp"
              alt="Coches y viajeros en la zona de salidas de una terminal de aeropuerto"
              className="absolute inset-0 h-full w-full object-cover object-center scale-105"
              width="1920"
              height="1080"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              data-testid="hero-airport-image"
              style={{ filter: 'grayscale(20%)' }}
            />
          </picture>
          {/* Dark overlay keeps the headline readable over the new photo */}
          <div className="absolute inset-0 bg-black/60" />
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
          {/* Gold accent glow */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#AAEE18]/10 rounded-full blur-[150px]" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center pt-20 pb-12">
          {/* Trust badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full mb-8 border border-white/10"
          >
            <Plane className="w-5 h-5 text-[#AAEE18]" aria-hidden="true" />
            <span className="text-sm text-white/90 font-medium">{t('landingHero.badge')}</span>
          </motion.div>

          {/* HEADLINE - Big and aggressive */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 max-w-5xl mx-auto"
          >
            <span className="text-white">{t('landingHero.headline1')}</span>
            <br />
            <span className="text-[#AAEE18]">{t('landingHero.headline2')}</span>
          </motion.h1>

          {/* SUBHEADLINE - Brutal clarity */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl text-white/80 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            {t('landingHero.subheadline1')} <span className="text-white font-semibold">{t('landingHero.subheadline2')}</span>.
            <br className="hidden sm:block" />
            {t('landingHero.subheadline3')}
          </motion.p>

          {/* Quick benefits - 3 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6 sm:gap-10 mb-12"
          >
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#AAEE18]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit1')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#AAEE18]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit2')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#AAEE18]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit3')}</span>
            </div>
          </motion.div>

          {/* MEGA CTA BUTTON */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
            className="mb-8"
          >
            <Button
              onClick={() => navigate('/reservar')}
              className="bg-[#AAEE18] hover:bg-[#94D410] text-black font-bold text-lg sm:text-xl px-10 sm:px-14 py-7 sm:py-8 rounded-full transition-all hover:scale-105 shadow-2xl shadow-[#AAEE18]/30 group"
              data-testid="hero-cta"
            >
              <span>{t('landingHero.cta')}</span>
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Microcopy under CTA */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-white/50 text-sm"
          >
            {t('landingHero.microcopy')}
          </motion.p>

          {/* Service signature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-12 inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm px-6 py-3 rounded-full border border-white/10"
          >
            <Car className="w-5 h-5 text-purple-400 shrink-0" aria-hidden="true" />
            <span className="text-white/70 text-sm">Tu coche en buenas manos, de la salida al regreso</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-white/40 flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="text-xs uppercase tracking-wider">{t('landingHero.discoverMore')}</span>
            <ChevronRight className="w-5 h-5 rotate-90" />
          </motion.div>
        </div>
      </section>

      {/* Original illustrative stories, not verified customer testimonials */}
      <section id="testimonials" className="py-12 lg:py-16 bg-gradient-to-b from-black to-[#111]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 px-4 py-2 rounded-full mb-4 border border-purple-400/20">
              <Plane className="w-4 h-4 text-purple-400" aria-hidden="true" />
              <span className="text-sm text-purple-300">Comentarios de ejemplo</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              Un valet para <span className="text-[#AAEE18]">cada forma de viajar</span>
            </h2>
            <p className="text-sm text-zinc-400 mt-4 max-w-2xl mx-auto" data-testid="illustrative-comments-disclaimer">
              Historias ficticias que ilustran el servicio. No son reseñas de clientes reales.
            </p>
          </div>

          {/* Reviews Grid - Horizontal scroll on mobile */}
          <div className="max-w-6xl mx-auto">
            <div className="flex lg:grid lg:grid-cols-3 gap-4 lg:gap-6 overflow-x-auto pb-4 lg:pb-0 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0">
              {[
                {
                  name: 'Nuria',
                  title: 'Las maletas al terminal, no al autobús',
                  text: 'Con los peques y todo el equipaje, lo que buscábamos era llegar a la terminal y empezar las vacaciones. Dejar el coche con el valet encajaba con nuestro viaje en familia.',
                  tag: 'Viaje en familia',
                  tagColor: 'bg-purple-500/20 text-purple-300'
                },
                {
                  name: 'Sergio',
                  title: 'La reunión en la cabeza, el parking resuelto',
                  text: 'Para un viaje de trabajo, prefiero organizar la entrega del coche antes de salir de casa. Fechas, terminal y reserva en el móvil: una gestión menos el día del vuelo.',
                  tag: 'Viaje de trabajo',
                  tagColor: 'bg-[#AAEE18]/15 text-[#AAEE18]'
                },
                {
                  name: 'Alicia',
                  title: 'Al volver, solo pienso en llegar a casa',
                  text: 'Después de unos días fuera, me resulta cómodo coordinar la devolución en el aeropuerto. Recoger el coche en el punto acordado y seguir mi camino es justo lo que necesito.',
                  tag: 'Regreso a casa',
                  tagColor: 'bg-blue-500/20 text-blue-300'
                },
              ].map((review, i) => (
                <motion.div
                  key={`review-${review.title.substring(0,10)}-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex-shrink-0 w-[300px] sm:w-[340px] lg:w-auto bg-white/5 border border-white/10 rounded-xl p-5 lg:p-6 snap-start hover:border-[#AAEE18]/30 transition-all"
                  data-testid={`review-card-${i}`}
                >
                  {/* Illustrative story + trip type */}
                  <div className="flex items-center justify-between mb-3">
                    <Car className="w-5 h-5 text-purple-400" aria-hidden="true" />
                    <span className={`text-xs px-2 py-1 rounded-full ${review.tagColor}`}>
                      {review.tag}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-white mb-2 text-sm lg:text-base">
                    «{review.title}»
                  </h3>

                  {/* Text */}
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    {review.text}
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#AAEE18]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#AAEE18] font-bold text-sm">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{review.name}</span>
                    </div>
                    <span className="text-xs text-zinc-400">Personaje ficticio</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Scroll hint for mobile */}
            <div className="flex lg:hidden justify-center mt-4">
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <ChevronRight className="w-4 h-4" />
                <span>Desliza para ver más</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          BENEFITS - Quick and clear
          ============================================ */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              El aeropuerto es tu destino. <span className="text-[#AAEE18]">El parking, nuestro trabajo.</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              No reservas solo una plaza: reservas la recogida, el aparcamiento y la devolución de tu coche para tu viaje.
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MapPin, text: 'Directo a tu terminal', desc: 'Acércate al punto de encuentro acordado en Madrid-Barajas. Nosotros recogemos el vehículo.' },
              { icon: Car, text: 'Un conductor se ocupa', desc: 'Entrega las llaves y continúa hacia tu vuelo mientras trasladamos el coche al aparcamiento.' },
              { icon: Plane, text: 'Tu regreso, coordinado', desc: 'Avísanos al volver para organizar la entrega del vehículo en el aeropuerto.' },
              { icon: Zap, text: 'Todo empieza online', desc: 'Indica fechas, terminal y datos del coche. Revisa tu reserva antes de confirmarla.' },
              { icon: Phone, text: 'Contacto directo', desc: 'Habla con el equipo para coordinar la recogida y resolver las dudas de tu estancia.' },
              { icon: CreditCard, text: 'El importe, por delante', desc: 'Consulta el precio de tu estancia y los extras elegidos antes de completar la reserva.' },
            ].map((item, i) => (
              <motion.div
                key={`benefit-${item.text.substring(0,10)}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#AAEE18]/30 transition-colors"
              >
                <item.icon className="w-10 h-10 text-[#AAEE18] mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.text}</h3>
                <p className="text-zinc-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          HOW IT WORKS - Super simple
          ============================================ */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0A] to-[#111]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              De tus llaves a tu vuelo, en tres pasos
            </h2>
            <p className="text-zinc-400">Así organizamos tu servicio valet con CG Valet Parking.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Cuéntanos cuándo vuelas',
                  desc: 'Selecciona tus fechas y la terminal. Añade los datos del vehículo y confirma tu estancia.',
                  icon: CreditCard
                },
                {
                  step: '2',
                  title: 'Nos vemos en la terminal',
                  desc: 'Llámanos 30 minutos antes de llegar. Coordina el encuentro, entrega las llaves y sigue hacia tu vuelo.',
                  icon: Car
                },
                {
                  step: '3',
                  title: 'Aterriza y sigue tu camino',
                  desc: 'Contacta con el equipo a tu regreso. Te indicaremos el punto de entrega para recuperar tu coche.',
                  icon: Plane
                },
              ].map((item, i) => (
                <motion.div
                  key={`step-${item.step}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative text-center"
                >
                  <div className="w-16 h-16 bg-[#AAEE18] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-black">{item.step}</span>
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#AAEE18] to-transparent" />
                  )}
                  <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                  <p className="text-zinc-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SOCIAL PROOF
          ============================================ */}
      <section className="py-20 bg-[#111]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
              {[
                { value: 'Terminal', label: 'El punto de encuentro de tu viaje' },
                { value: 'Valet', label: 'Recogemos y aparcamos por ti' },
                { value: 'Online', label: 'Organiza tu estancia antes de salir' },
                { value: 'Regreso', label: 'Coordinamos la devolución contigo' },
              ].map((stat, i) => (
                <motion.div
                  key={`stat-${stat.label}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <p className="text-3xl sm:text-4xl font-bold text-[#AAEE18]">{stat.value}</p>
                  <p className="text-zinc-400 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Service essentials */}
            <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pt-12 border-t border-white/10">
              <div className="flex items-center gap-2 text-zinc-400">
                <Car className="w-5 h-5" />
                <span className="text-sm">Servicio con conductor</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Phone className="w-5 h-5" />
                <span className="text-sm">Contacto con el equipo</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <CreditCard className="w-5 h-5" />
                <span className="text-sm">Importe antes de confirmar</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          URGENCY + FINAL CTA
          ============================================ */}
      <section className="py-20 bg-gradient-to-b from-[#111] to-black relative overflow-hidden">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#AAEE18]/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-300 px-4 py-2 rounded-full mb-8">
              <Plane className="w-4 h-4" />
              <span className="text-sm font-medium">Madrid-Barajas, punto de partida</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Tu próximo vuelo empieza
              <br /><span className="text-[#AAEE18]">con las llaves en nuestras manos.</span>
            </h2>

            <p className="text-xl text-zinc-400 mb-10">
              Organiza la recogida de tu coche con CG Valet Parking. Tú prepara el viaje; nosotros nos ocupamos de aparcar.
            </p>

            <Button
              onClick={() => navigate('/reservar')}
              className="bg-[#AAEE18] hover:bg-[#94D410] text-black font-bold text-xl px-12 py-7 transition-all hover:scale-105 shadow-lg shadow-[#AAEE18]/20"
              data-testid="final-cta"
            >
              Organizar mi recogida
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>

            <p className="text-zinc-500 text-sm mt-6">
              Fechas, terminal y datos del coche. Tu servicio valet, en una sola reserva.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          MINIMAL FOOTER
          ============================================ */}
      <footer className="py-8 bg-black border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/brand/cg-valet-parking.png" alt="CG Valet Parking" className="h-16 w-auto object-contain" />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <a href="/terminos" className="text-zinc-400 hover:text-primary transition-colors">
                Términos y Condiciones
              </a>
              <span className="text-zinc-700">|</span>
              <a href="/privacidad" className="text-zinc-400 hover:text-primary transition-colors">
                Privacidad
              </a>
              <span className="text-zinc-700">|</span>
              <a href={`mailto:${BRAND.contactEmail}`} className="text-zinc-400 hover:text-primary transition-colors">
                Contacto
              </a>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-400">
            <a href={BRAND.websiteUrl} className="hover:text-primary transition-colors">{BRAND.website}</a>
            <a href={`mailto:${BRAND.contactEmail}`} className="hover:text-primary transition-colors">{BRAND.contactEmail}</a>
            <a href={`mailto:${BRAND.bookingEmail}`} className="hover:text-primary transition-colors">Reservas: {BRAND.bookingEmail}</a>
          </div>
          <div className="mt-4 text-center">
            <p className="text-zinc-500 text-sm">
              © 2026 CG Valet Parking · Valet Parking Aeropuerto Madrid
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
